import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  leiturasDaVertical, verVertical,
  type CampanhaCompleta, type LeiturasDaVertical, type VerticalCompleta, type VerticalDaCampanha,
} from '../../api/cptu';
import {
  PREMISSAS_PADRAO, premissasEfetivas, processarVertical,
  type Edicao, type Leituras, type Premissas, type Resultado,
} from '../../cptu/motor';

/**
 * A interpretação de uma vertical na tela — onda 313, Tarefa 313.4 (spec 07 §6.1, item 4; R2, R9, R10).
 *
 * O resultado é do navegador (R2): a tela lê as leituras do equipamento (`GET /cptu/verticais/:id/leituras`),
 * junta as premissas da campanha às da vertical e à edição gravada, e calcula com o motor do `web` — o mesmo,
 * byte a byte, do servidor e do Portal. A API guarda só as escolhas do engenheiro, nunca coluna calculada.
 *
 * «Simular» é um rascunho (R10): premissas, NA, cota e edição que ainda não foram gravadas. O gravado é o
 * «antes» (cinza tracejado nas trilhas e no ábaco), o rascunho é o «depois», recalculado 150 ms depois da
 * última mudança (R9: o CPTu01, 1.788 leituras, processa em ≈ 23 ms). Gravar manda as premissas SEMPRE
 * inteiras, a partir do gravado — o PATCH troca o objeto todo, e nada do que já estava pode sumir:
 *  · na campanha, `{ ...campanha.premissas, ...mudancas }`;
 *  · na vertical, `{ ...(vertical.premissas sem o NA e a cota), ...mudancas }` (o NA e a cota vão pelo
 *    cabeçalho, `nivelAgua` e `cota`).
 */

/** As vistas da vertical (R3): Perfis, Tabela — e Edição e Camadas, da Tarefa 313.6. */
export type VistaDaVertical = 'perfis' | 'tabela' | 'edicao' | 'camadas';

/** O endereço da vertical aberta (sub-rota da aba Verticais, R3): `/cptu/:id/verticais/:vid[/vista]`. */
export function rotaDaVertical(campanhaId: string, verticalId: string, vista: VistaDaVertical = 'perfis'): string {
  const base = `/cptu/${campanhaId}/verticais/${verticalId}`;
  return vista === 'perfis' ? base : `${base}/${vista}`;
}

/** O rascunho de «Simular»: só o que mudou (premissas, NA, cota, edição); o resto é o gravado. */
export interface Rascunho {
  premissas?: Partial<Premissas>;
  nivelAgua?: number | null;
  cota?: number | null;
  edicao?: Edicao;
}

const convertidas = new WeakMap<LeiturasDaVertical, Leituras>();

/** Uma coluna da API (null = sem leitura) como o motor a lê: Float64Array com NaN no lugar do null. */
const paraSerie = (s: readonly (number | null)[]): Float64Array =>
  Float64Array.from(s, (v) => (v === null || v === undefined || !Number.isFinite(Number(v)) ? NaN : Number(v)));

/**
 * As leituras da API como o motor as lê — null vira NaN, cada canal um `Float64Array`, na ordem do arquivo.
 * A conversão fica guardada por resposta (as leituras não mudam: são o bruto, RN-253).
 */
export function leiturasParaOMotor(l: LeiturasDaVertical): Leituras {
  const ja = convertidas.get(l);
  if (ja) return ja;
  const leituras: Leituras = {
    profundidade: paraSerie(l.profundidade), qc: paraSerie(l.qc), fs: paraSerie(l.fs), u2: paraSerie(l.u2),
  };
  const temValor = (s: readonly (number | null)[] | undefined) => !!s && s.some((v) => v !== null);
  if (temValor(l.inclinacao)) leituras.inclinacao = paraSerie(l.inclinacao);
  if (temValor(l.velocidade)) leituras.velocidade = paraSerie(l.velocidade);
  if (temValor(l.tempo)) leituras.tempo = paraSerie(l.tempo);
  convertidas.set(l, leituras);
  return leituras;
}

/** As leituras de cada vertical aberta nesta sessão: o bruto não muda, e voltar a uma vertical não as relê. */
const LEITURAS_DA_SESSAO = new Map<string, Promise<LeiturasDaVertical>>();

function carregarLeituras(id: string): Promise<LeiturasDaVertical> {
  let p = LEITURAS_DA_SESSAO.get(id);
  if (!p) {
    p = leiturasDaVertical(id);
    p.catch(() => LEITURAS_DA_SESSAO.delete(id));
    LEITURAS_DA_SESSAO.set(id, p);
  }
  return p;
}

/** As leituras de uma vertical como o motor as lê, pelo cache da sessão (o bruto não muda) — Tarefa 313.5. */
export function leiturasDaSessao(id: string): Promise<Leituras> {
  return carregarLeituras(id).then(leiturasParaOMotor);
}

/** As zonas SBTn das verticais já abertas nesta sessão (a faixa da lista das verticais). */
const ZONAS_DA_SESSAO = new Map<string, { z: Float64Array; sbtn: Int8Array }>();

/** A faixa SBTn de uma vertical já aberta nesta sessão, ou null. */
export function zonasDaSessao(verticalId: string): { z: Float64Array; sbtn: Int8Array } | null {
  return ZONAS_DA_SESSAO.get(verticalId) ?? null;
}

/** As premissas gravadas da vertical sem o NA e a cota: as exceções à campanha (o NA e a cota são do cabeçalho). */
export function excecoesDaVertical(v: VerticalDaCampanha): Partial<Premissas> {
  const p: Partial<Premissas> = { ...(v.premissas ?? {}) };
  delete p.na;
  delete p.cota;
  return p;
}

/** As premissas inteiras para o PATCH da campanha, a partir do gravado (R10). */
export function premissasParaACampanha(c: CampanhaCompleta, mudancas: Partial<Premissas>): Partial<Premissas> {
  return { ...(c.campanha.premissas ?? {}), ...mudancas };
}

/** As premissas inteiras para o PATCH da vertical, a partir do gravado, sem o NA e a cota (R10). */
export function premissasParaAVertical(v: VerticalDaCampanha, mudancas: Partial<Premissas>): Partial<Premissas> {
  return { ...excecoesDaVertical(v), ...mudancas };
}

/** As premissas da vertical como o motor as recebe: as exceções, e o NA e a cota do cabeçalho. */
function premissasDaVertical(v: VerticalDaCampanha, r?: Rascunho | null): Partial<Premissas> {
  const p: Partial<Premissas> = { ...(v.premissas ?? {}) };
  if (v.nivel_agua !== null && v.nivel_agua !== undefined) p.na = v.nivel_agua;
  p.cota = v.cota ?? null;
  if (r?.premissas) Object.assign(p, r.premissas);
  if (r && r.nivelAgua !== undefined) p.na = r.nivelAgua ?? PREMISSAS_PADRAO.na;
  if (r && r.cota !== undefined) p.cota = r.cota;
  return p;
}

/**
 * O resultado GRAVADO de uma vertical da campanha — as premissas da campanha, as exceções da vertical, o NA e a cota
 * do cabeçalho e a edição gravada —, o mesmo que a tela da vertical desenha sem rascunho (R2). A dissipação (Tarefa
 * 313.5) tira dele o u₀, o G₀, o Su e o M da vertical na profundidade.
 */
export function resultadoGravado(c: CampanhaCompleta, v: VerticalDaCampanha, leituras: Leituras): Resultado {
  return processarVertical({
    leituras,
    premissas: premissasEfetivas(c.campanha.premissas ?? {}, premissasDaVertical(v)),
    edicao: v.edicao,
  });
}

/**
 * O foco no primeiro dos seletores que estiver à vista, logo depois de a tela se refazer — para onde o foco vai quando
 * o botão apertado some («Sair sem gravar…», «Limpar a sugestão»): o próximo controle útil, nunca o `<body>` (rodada do
 * agent-browser da 313, I2). Um `setTimeout` e não o `requestAnimationFrame`, que a aba fora de vista não chama.
 */
export function focarOPrimeiro(...seletores: string[]): void {
  setTimeout(() => {
    for (const s of seletores) {
      const el = document.querySelector<HTMLElement>(s);
      if (el && el.getClientRects().length > 0) {
        el.focus();
        return;
      }
    }
  }, 0);
}

/**
 * O foco de volta a quem abriu a janela (rodada do agent-browser da 313, I2). Quem usa o `Dialogo` da casa o tira da
 * árvore ainda aberto, e o navegador só devolve o foco no `close()`: sem isto o foco caía no `<body>` e o teclado
 * voltava ao topo da página a cada janela (Esc, ✕ ou «Cancelar»). Guarda o elemento com o foco quando a janela abre — o
 * efeito de layout roda antes do `showModal`, que é de um efeito do filho — e o devolve quando ela fecha; se ele saiu
 * da tela (a ação sumiu com a gravação), o foco vai ao primeiro de `reserva` à vista.
 */
export function useFocoDeVolta(aberta: boolean, ...reserva: string[]): void {
  const origem = useRef<HTMLElement | null>(null);
  const estava = useRef(false);
  useLayoutEffect(() => {
    if (aberta && !estava.current) {
      const a = document.activeElement;
      origem.current = a instanceof HTMLElement && a !== document.body ? a : null;
    } else if (!aberta && estava.current) {
      const alvo = origem.current;
      origem.current = null;
      if (alvo && alvo.isConnected && alvo.getClientRects().length > 0) alvo.focus();
      else if (reserva.length > 0) focarOPrimeiro(...reserva);
    }
    estava.current = aberta;
    // A reserva é um texto fixo de quem chama: só a janela abrir ou fechar importa.
  }, [aberta]); // eslint-disable-line react-hooks/exhaustive-deps
}

/** O tempo entre a última mudança do rascunho e o recálculo (R9). */
export const ESPERA_DO_RECALCULO = 150;

/**
 * A interpretação de uma vertical: a vertical (com as camadas e o impedimento do cone), as leituras, o
 * resultado gravado e o do rascunho. `simular(r)` troca o rascunho e recalcula 150 ms depois; `simular(null)`
 * descarta. `recarregar()` relê a vertical (as leituras ficam: são o bruto).
 */
export function useInterpretacao(c: CampanhaCompleta, verticalId: string): {
  vertical: VerticalCompleta | null;
  leituras: Leituras | null;
  erro: unknown;
  resultado: Resultado | null;
  rascunho: Rascunho | null;
  doRascunho: Resultado | null;
  simular(r: Rascunho | null): void;
  recarregar(): Promise<void>;
} {
  const [vertical, setVertical] = useState<VerticalCompleta | null>(null);
  const [leituras, setLeituras] = useState<Leituras | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [rascunho, setRascunho] = useState<Rascunho | null>(null);
  const [doRascunho, setDoRascunho] = useState<Resultado | null>(null);

  const recarregar = useCallback(async () => {
    try {
      const [v, l] = await Promise.all([verVertical(verticalId), carregarLeituras(verticalId)]);
      setVertical(v);
      setLeituras(leiturasParaOMotor(l));
      setErro(null);
    } catch (e) { setErro(e); }
  }, [verticalId]);
  useEffect(() => { void recarregar(); }, [recarregar]);

  const daCampanha = c.campanha.premissas;
  const resultado = useMemo(() => {
    if (!vertical || !leituras) return null;
    return processarVertical({
      leituras,
      premissas: premissasEfetivas(daCampanha ?? {}, premissasDaVertical(vertical)),
      edicao: vertical.edicao,
    });
  }, [vertical, leituras, daCampanha]);

  useEffect(() => {
    if (resultado) ZONAS_DA_SESSAO.set(verticalId, { z: resultado.col.z, sbtn: resultado.sbtn });
  }, [resultado, verticalId]);

  // O rascunho recalcula 150 ms depois da última mudança; sem rascunho, nada a recalcular.
  useEffect(() => {
    if (!rascunho || !vertical || !leituras) {
      setDoRascunho(null);
      return undefined;
    }
    const t = setTimeout(() => {
      setDoRascunho(processarVertical({
        leituras,
        premissas: premissasEfetivas(daCampanha ?? {}, premissasDaVertical(vertical, rascunho)),
        edicao: rascunho.edicao !== undefined ? rascunho.edicao : vertical.edicao,
      }));
    }, ESPERA_DO_RECALCULO);
    return () => clearTimeout(t);
  }, [rascunho, vertical, leituras, daCampanha]);

  const simular = useCallback((r: Rascunho | null) => setRascunho(r), []);

  return { vertical, leituras, erro, resultado, rascunho, doRascunho: rascunho ? doRascunho : null, simular, recarregar };
}
